import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Section, ToneChip, Meter, StatusChip, fmtL, fmtP, mLabel, TONES } from "./BpKit";

const tip = { contentStyle: { background: "#fff", border: "1px solid #cbd5e1", borderRadius: 2, fontSize: 12 } };
const TICK = { fill: "#64748b", fontSize: 11 };

const Tile = ({ label, value, hint, tid, tone }) => (
  <div className="bg-white border border-slate-300 rounded-sm p-4 ax-fade" data-testid={tid}>
    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="num text-2xl sm:text-[28px] font-bold tracking-tight mt-2 text-slate-900" style={{ color: tone }}>{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export const KpiRow = ({ k }) => {
  const toneHex = (TONES[k.tone] || [])[2];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <Tile tid="bp-kpi-fy-target" label="FY target" value={fmtL(k.fy_target)} hint={k.team_plans_total ? `Salespeople's plans ${fmtL(k.team_plans_total)}` : "Set in the branch plan"} />
      <Tile tid="bp-kpi-booked-todate" label="Booked to date" value={fmtL(k.booked)} hint={k.fy_target ? `${fmtP(k.fy_pct)} of the full year` : "PO values booked this FY"} />
      <Tile tid="bp-kpi-achievement-pct" label="Achieved vs plan to date" value={fmtP(k.pct)} tone={toneHex} hint={`Plan to date ${fmtL(k.target_to_date)}`} />
      <Tile tid="bp-kpi-required-runrate" label="Needed per month" value={k.run_rate == null ? "—" : fmtL(k.run_rate)} hint={`To hit the FY target · ${k.months_left} month${k.months_left === 1 ? "" : "s"} left`} />
      <Tile tid="bp-kpi-yoy-growth" label="Growth vs last year" value={k.growth == null ? "—" : `${k.growth > 0 ? "+" : ""}${k.growth}%`} tone={k.growth == null ? undefined : k.growth >= 0 ? "#059669" : "#e11d48"} hint={`Same period last year ${fmtL(k.ly_to_date)}`} />
      <Tile tid="bp-kpi-current-month" label={`${mLabel(k.month)} booked`} value={fmtL(k.month_booked)} hint={k.month_target ? `of ${fmtL(k.month_target)} · ${fmtP(k.month_pct)}` : "No target this month"} />
    </div>
  );
};

export const PlanChart = ({ series }) => (
  <Section tid="bp-chart-monthly-plan-actual" title="Monthly plan vs actual" hint="Bars are ₹ Lakhs booked each month; the line shows how much of the plan to date has been achieved.">
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series.map((s) => ({ ...s, label: mLabel(s.month) }))} margin={{ left: 0, right: 8, top: 8 }}>
          <CartesianGrid stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} />
          <YAxis yAxisId="v" tick={TICK} axisLine={false} tickLine={false} width={44} />
          <YAxis yAxisId="p" orientation="right" tick={TICK} axisLine={false} tickLine={false} unit="%" width={44} />
          <Tooltip {...tip} formatter={(v, n) => (n === "Achieved to date" ? fmtP(v) : fmtL(v))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="v" dataKey="plan" name="Plan" fill="#cbd5e1" radius={[2, 2, 0, 0]} />
          <Bar yAxisId="v" dataKey="actual" name="Booked" fill="hsl(14 88% 56%)" radius={[2, 2, 0, 0]} />
          <Line yAxisId="v" dataKey="ly" name="Last year" stroke="#64748b" strokeDasharray="4 3" dot={false} strokeWidth={1.5} />
          <Line yAxisId="p" dataKey="cum_pct" name="Achieved to date" stroke="#0284c7" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Section>
);

export const BranchCards = ({ cards }) => (
  <Section tid="bp-branch-split-card" title="By branch" hint="Plan to date vs PO bookings">
    <div className="space-y-4">
      {cards.map((c) => (
        <div key={c.branch} data-testid={`bp-branch-card-${c.branch}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><span className="font-semibold text-sm">{c.label}</span><StatusChip s={c.plan_status} tid={`bp-branch-card-status-${c.branch}`} /></div>
            <ToneChip t={c.tone} tid={`bp-branch-card-tone-${c.branch}`} />
          </div>
          <div className="num text-sm mt-1.5"><b>{fmtL(c.booked)}</b> <span className="text-slate-500">of {fmtL(c.target_to_date)} to date · FY {fmtL(c.fy_target)}</span> <b className="ml-1">{fmtP(c.pct)}</b></div>
          <div className="mt-2"><Meter pct={c.pct} tone={c.tone} tid={`bp-branch-card-meter-${c.branch}`} /></div>
          <div className="text-xs text-slate-500 mt-1.5">This month {fmtL(c.month_booked)} of {fmtL(c.month_target)}{c.source === "sales plans" && c.fy_target ? " · target from salespeople's plans" : ""}</div>
        </div>
      ))}
    </div>
  </Section>
);

export const SegmentCard = ({ rows }) => (
  <Section tid="bp-segment-card" title="New vs repeat business" hint="Plan to date vs PO bookings">
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.segment} data-testid={`bp-segment-${r.segment}`}>
          <div className="flex justify-between text-sm"><span className="font-semibold">{r.segment === "new" ? "New business" : "Repeat business"}</span><span className="num font-bold">{fmtP(r.pct)}</span></div>
          <div className="num text-xs text-slate-500 mt-1">{fmtL(r.booked)} of {fmtL(r.target_to_date)} to date · FY {fmtL(r.fy_target)}</div>
          <div className="mt-2"><Meter pct={r.pct} tone={r.pct == null ? null : r.pct >= 95 ? "on_track" : r.pct >= 80 ? "at_risk" : "behind"} /></div>
        </div>
      ))}
    </div>
  </Section>
);
